import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Alert, Grid, PageBreadcrumb, PageContent, PageTitle, Spacer } from '@zillow/constellation';
import { Heading, Modal, Text, TextButton } from '@zillow/constellation-10';
import { parseQueryString } from '@zg-rentals/string-utils';

import { useSelector } from '../../selectors/useSelector';

import HeadWrapper from '../../components/HeadWrapper';
import SpinnerContainer from '../../components/SpinnerContainer';
import SubmitRequestForm from '../../components/SubmitRequestForm';
import HelpArticlesContent from '../../components/HelpArticlesContent';

import SubmitRequestActions from '../../actions/SubmitRequestActions';

import {
  useGetHelpArticles,
  useGetHelpTopics,
  useSubmitRequest,
  useSubmitUserRequest,
} from '../../hooks/queries/support';

import { HelpCategory, HelpTopic } from '../../model/HelpTopics';
import type { HelpCategoryProps, HelpTopicProps } from '../../model/HelpTopics';

import { StyledPage } from './StyledComponents';

const SubmitRequestPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    user: { loggedIn: isLoggedIn },
    submitRequest: { selectedHelpCategory, selectedHelpTopic },
  } = useSelector((state) => {
    return { user: state.user, submitRequest: state.submitRequest };
  }, shallowEqual);

  const [submitRequestError, setSubmitRequestError] = useState('');
  const [isCategoryReadOnly, setIsCategoryReadOnly] = useState(false);
  const [isTopicReadOnly, setIsTopicReadOnly] = useState(false);

  const { data: helpTopics, isLoading: isHelpTopicsLoading, error: helpTopicsError } = useGetHelpTopics({ isLoggedIn });

  //  blank category will retrieve FAQ for the initial page state
  const { error: helpArticlesError } = useGetHelpArticles({
    category: selectedHelpCategory?.key || '',
    topic: selectedHelpTopic?.key || '',
  });

  const {
    mutate: postSubmitRequest,
    isPending: isSubmittingRequest,
    isSuccess: isSubmitRequestSuccess,
    reset: resetSubmitRequest,
  } = useSubmitRequest({
    onError: (response: { error: { message: string } }) => setSubmitRequestError(response.error.message),
  });

  const {
    mutate: postSubmitUserRequest,
    isPending: isSubmittingUserRequest,
    isSuccess: isSubmitUserRequestSuccess,
    reset: resetSubmitUserRequest,
  } = useSubmitUserRequest({
    onError: (response: { error: { message: string } }) => setSubmitRequestError(response.error.message),
  });

  const queryObject = parseQueryString(location.search);
  const {
    category: queryCategoryKey,
    topic: queryTopicKey,
    listingAddress: queryListingAddress,
    unit: queryUnit,
    listingAlias: queryListingAlias,
  } = queryObject;

  useEffect(() => {
    if (helpTopics && queryCategoryKey) {
      const category = helpTopics?.categories?.find((c: HelpCategoryProps) => c.key === queryCategoryKey);
      if (category) {
        dispatch(SubmitRequestActions.updateSelectedHelpCategory(HelpCategory(category)));
        setIsCategoryReadOnly(true);
      }
      if (queryTopicKey) {
        const topic = category?.topics?.find((t: HelpTopicProps) => t.key === queryTopicKey);
        if (topic) {
          dispatch(SubmitRequestActions.updateSelectedHelpTopic(HelpTopic(topic)));
          setIsTopicReadOnly(true);
        }
      }
    }
  }, [helpTopics]);

  const resetForm = () => {
    dispatch(SubmitRequestActions.updateSelectedHelpCategory(HelpCategory({ key: '', description: '' })));
    dispatch(SubmitRequestActions.updateSelectedHelpTopic(HelpTopic({ key: '', description: '' })));
    if (isLoggedIn) {
      resetSubmitUserRequest();
    }
    resetSubmitRequest();
  };

  const renderSuccess = () => (
    <Spacer padding="md">
      <Alert
        appearance="success"
        body={
          <Text>
            Thank you! Your request has been submitted.{' '}
            <TextButton onClick={resetForm}>Submit another request.</TextButton>
          </Text>
        }
      />
    </Spacer>
  );

  const renderForm = () => (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)">
      <Grid gridColumnEnd={{ default: 'span 12', xl_gte: 'span 8' }}>
        <SubmitRequestForm
          onSubmit={isLoggedIn ? postSubmitUserRequest : postSubmitRequest}
          queryListingAddress={queryListingAddress || ''}
          queryUnit={queryUnit || ''}
          queryListingAlias={queryListingAlias || ''}
          isCategoryReadOnly={isCategoryReadOnly}
          isTopicReadOnly={isTopicReadOnly}
          isSubmitting={Boolean(isSubmittingRequest || isSubmittingUserRequest)}
        />
      </Grid>
      <Grid gridColumnEnd={{ default: 'span 12', xl_gte: 'span 4' }}>
        <HelpArticlesContent />
      </Grid>
      {submitRequestError && (
        <Modal
          open={true}
          onOpenChange={(open) => !open && setSubmitRequestError('')}
          header={<Heading level={3}>Unable to submit request</Heading>}
          body={<Text>{submitRequestError}</Text>}
        />
      )}
    </Grid>
  );

  const renderState = () => {
    switch (true) {
      case Boolean(helpArticlesError || helpTopicsError):
        return <Alert appearance="error" body="Something went wrong; please try again later" />;
      case Boolean(isSubmitRequestSuccess || isSubmitUserRequestSuccess):
        return renderSuccess();
      case Boolean(isHelpTopicsLoading):
        return <SpinnerContainer size="lg" height="600px" />;
      case Boolean(helpTopics?.categories?.length):
        return renderForm();
      default:
        return null;
    }
  };

  return (
    <>
      <HeadWrapper title="Zillow Rental Manager" />
      <StyledPage data-testid="SubmitRequestPage" backgroundColor="white">
        <PageBreadcrumb
          backButton={
            <TextButton asChild>
              <a href="https://help.zillowrentalmanager.com/hc/en-us">Help center | Zillow Rental Manager</a>
            </TextButton>
          }
        />
        <PageTitle
          heading={
            <Heading level={1} fontFamily="heading-alt" textStyle="heading-md">
              Submit a request
            </Heading>
          }
        />
        <PageContent>{renderState()}</PageContent>
      </StyledPage>
    </>
  );
};

export default SubmitRequestPage;
